import { createFileRoute } from '@tanstack/react-router'
import { AIShell } from '@/components/layout/ai-shell'
import { useRef, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { chatActions } from '@/store/ai-chat.store'
import {
  useActiveMessages,
  useFilteredThreads,
  useChatStore,
  useActiveThread,
} from '@/hooks/useChat'
import {
  ChatSidebar,
  ChatHeader,
  ChatInput,
  MessageBubble,
  WelcomeScreen,
} from '@/components/features/ai'

export const Route = createFileRoute('/rosewood-ai')({
  component: RosewoodAIPage,
})

export function RosewoodAIPage() {
  const {
    sidebarOpen,
    activeThreadId,
    threadSearch,
    threadFilter,
    settings,
    inputValue,
    isStreaming,
  } = useChatStore()

  const threads = useFilteredThreads()
  const messages = useActiveMessages()
  const activeThread = useActiveThread()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, isStreaming])

  const handleSend = (text: string) => chatActions.sendMessage(text)

  const handlePromptSelect = (prompt: string) => {
    chatActions.setInputValue(prompt)
    chatActions.sendMessage(prompt)
  }

  const showWelcome = !activeThreadId && messages.length <= 1

  return (
    <AIShell>
      <AnimatePresence mode="wait">
        {sidebarOpen && (
          <motion.div
            key="sidebar"
            initial={{ width: 0 }}
            animate={{ width: 280 }}
            exit={{ width: 0 }}
            transition={{ duration: 0.22 }}
            className="flex-shrink-0 overflow-hidden"
          >
            <ChatSidebar
              threads={threads}
              activeThreadId={activeThreadId}
              threadSearch={threadSearch}
              threadFilter={threadFilter}
              settings={settings}
              isOpen={sidebarOpen}
              onToggle={chatActions.toggleSidebar}
              onNewChat={chatActions.startNewChat}
              onSelectThread={chatActions.setActiveThread}
              onPinThread={chatActions.pinThread}
              onDeleteThread={chatActions.deleteThread}
              onSearchChange={chatActions.setThreadSearch}
              onFilterChange={chatActions.setThreadFilter}
              onSettingsChange={chatActions.updateSettings}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <ChatHeader
          activeThread={activeThread ?? null}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={chatActions.toggleSidebar}
          ctaHref="/contact"
          ctaLabel="Talk to a CPA"
        />

        <div className="flex-1 overflow-y-auto">
          {showWelcome ? (
            <WelcomeScreen onPromptSelect={handlePromptSelect} />
          ) : (
            <div className="max-w-3xl mx-auto px-3 sm:px-4 py-6 space-y-6">
              <AnimatePresence mode="popLayout">
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    onFeedback={(id, type) =>
                      chatActions.setMessageFeedback(
                        activeThreadId ?? 'new',
                        id,
                        type
                      )
                    }
                    ctaHref="/contact"
                    ctaLabel="Talk to a CPA"
                  />
                ))}
              </AnimatePresence>
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <ChatInput
          value={inputValue}
          onChange={chatActions.setInputValue}
          onSend={handleSend}
          onStop={() => {}}
          isStreaming={isStreaming}
        />
      </div>
    </AIShell>
  )
}